import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  ConversationFooterBottomRowStyled,
  ConversationFooterDisclaimerStyled,
  ConversationFooterInputBoxStyled,
  ConversationFooterInputFieldStyled,
  ConversationFooterSubmitButtonStyled,
  ConversationFooterWrapperStyled,
} from './styles/ConversationFooterStyled'
import { ConversationStreamStatuses } from '../../../../hooks/useConversationStream'
import { useConversation } from '../../../../providers/ConversationProvider'

export const ConversationFooter = () => {
  const intl = useProcurifyIntl()
  const { sendMessage, status } = useConversation()
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const isAwaitingTurn =
    status === ConversationStreamStatuses.STREAMING ||
    status === ConversationStreamStatuses.IDLE
  const isSubmittable = !isAwaitingTurn && value.trim() !== ''

  useEffect(() => {
    if (!isAwaitingTurn) {
      inputRef.current?.focus()
    }
  }, [isAwaitingTurn])

  const resizeToContent = useCallback(() => {
    const input = inputRef.current
    if (!input) return
    input.style.height = 'auto'
    input.style.height = `${input.scrollHeight}px`
  }, [])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value)
      resizeToContent()
    },
    [resizeToContent]
  )

  const handleSubmit = useCallback(() => {
    if (isAwaitingTurn || value.trim() === '') {
      return
    }
    sendMessage(value)
    setValue('')
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }
  }, [isAwaitingTurn, value, sendMessage])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <ConversationFooterWrapperStyled>
      <ConversationFooterInputBoxStyled>
        <ConversationFooterInputFieldStyled
          ref={inputRef}
          data-analytics-id='conversation-message-input'
          rows={1}
          aria-label={intl.formatMessage(messages.inputAriaLabel)}
          placeholder={intl.formatMessage(messages.inputPlaceholder)}
          value={value}
          disabled={isAwaitingTurn}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <ConversationFooterBottomRowStyled>
          <ConversationFooterSubmitButtonStyled
            analyticsId='conversation-send-message-button'
            aria-label={intl.formatMessage(messages.submitAriaLabel)}
            icon='pointer-up'
            onClick={handleSubmit}
            disabled={!isSubmittable}
          />
        </ConversationFooterBottomRowStyled>
      </ConversationFooterInputBoxStyled>
      <ConversationFooterDisclaimerStyled>
        {intl.formatMessage(messages.disclaimer)}
      </ConversationFooterDisclaimerStyled>
    </ConversationFooterWrapperStyled>
  )
}
